import React from 'react';

import { useSelector } from 'react-redux';
import { TextField, MenuItem, InputAdornment, SxProps, InputProps } from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { RiMapPinLine } from 'react-icons/ri';

import * as UserSelectors from 'selectors/UserSelectors';
import { useTranslation } from 'services/localization/useTranslation';

interface Props {
	value: 'all' | string;
	onChange: (location: string) => void;
	hideAll?: boolean;
	showLabel?: boolean;
	hideIcon?: boolean;
	fullWidth?: boolean;
	variant?: 'standard' | 'filled' | 'outlined';
	size?: 'small' | 'normal';
	sx?: SxProps<Theme>;
	inputProps?: InputProps;
}

const ShopLocationSelect = ({ variant = 'standard', size = 'normal', ...props }: Props) => {
	const { t } = useTranslation();
	const classes = useStyles({ size });

	const locations = useSelector(UserSelectors.userLocations);
	const userHasAccessToAllLocations = useSelector(UserSelectors.userHasAccessToAllLocations);

	return locations.length <= 1 ? null : (
		<TextField
			variant="standard"
			select
			SelectProps={{
				...(variant !== 'outlined' && { disableUnderline: true }),
			}}
			label={props.showLabel ? t('common:shopLocation.store', 'Store') : undefined}
			value={props.value}
			fullWidth={props.fullWidth}
			onChange={(
				event: React.ChangeEvent<{
					name?: string | 'all';
					value: unknown;
				}>,
			) => {
				props.onChange(event.target.value as string);
			}}
			InputProps={{
				startAdornment: props.hideIcon ? undefined : (
					<InputAdornment position="start">
						<RiMapPinLine className={classes.icon} />
					</InputAdornment>
				),
				classes: {
					root: classes.textField,
				},
				...props.inputProps,
			}}
			sx={props.sx}
		>
			{!props.hideAll && userHasAccessToAllLocations && (
				<MenuItem value={'all'}>{t('common:shopLocation.allStores', 'All stores')}</MenuItem>
			)}

			{locations.map((location) => (
				<MenuItem key={location.id} value={location.id}>
					{location.name || location.address}
				</MenuItem>
			))}
		</TextField>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		icon: {
			fontSize: '1.8rem',
			marginBottom: 2,
			color: 'inherit',
		},
		textField: {
			minWidth: 0,
			height: ({ size }: { size?: Props['size'] }) => (size === 'small' ? 40 : undefined),
			fontSize: ({ size }: { size?: Props['size'] }) => (size === 'small' ? '1.4rem' : undefined),
		},
	}),
);

export default ShopLocationSelect;
